import React from 'react';
import { Switch, FormControlLabel } from '@mui/material';
import { FlagSwitchProps } from './FlagSwitch.types';

/**
 * FlagSwitch - Componente global para switches/flags del sistema
 *
 * Encapsula el diseño consistente de switches con:
 * - Círculo del mismo tamaño que la altura de la barra
 * - Verde brillante cuando está activo (#34d399)
 * - Diseño moderno y limpio
 *
 * El diseño se controla desde el theme global, por lo que cambiar
 * el diseño aquí afectará a todos los switches del sistema.
 *
 * @example
 * // Uso básico
 * <FlagSwitch
 *   checked={isEnabled}
 *   onChange={(e, checked) => setIsEnabled(checked)}
 * />
 *
 * // Con label
 * <FlagSwitch
 *   checked={isEnabled}
 *   onChange={(e, checked) => setIsEnabled(checked)}
 *   showLabel
 *   label="Activar opción"
 * />
 *
 * // Con color personalizado
 * <FlagSwitch
 *   checked={isEnabled}
 *   onChange={(e, checked) => setIsEnabled(checked)}
 *   activeColor="primary"
 * />
 */
export const FlagSwitch: React.FC<FlagSwitchProps> = ({
  checked,
  onChange,
  size = 'small',
  disabled = false,
  activeColor = 'success',
  showLabel = false,
  label,
  labelPlacement = 'end',
  ...otherProps
}) => {
  const switchComponent = (
    <Switch
      checked={checked}
      onChange={onChange}
      size={size}
      disabled={disabled}
      color={activeColor}
      {...otherProps}
    />
  );

  if (showLabel && label) {
    return (
      <FormControlLabel
        control={switchComponent}
        label={label}
        labelPlacement={labelPlacement}
        sx={{
          gap: 1.25, // 10px - Espaciado entre el switch y el label
        }}
      />
    );
  }

  return switchComponent;
};
